// Rule sourcing gate — which agreements have a VETTED, sourced rule catalog.
//
// Only catalogued agreements may DISPLAY or STORE quoted agreement text. For every other
// agreement, any "exact language" returned by the analyzeClaimRules AI is UNSOURCED and must
// never be shown or stored as agreement language (PD-4 / PD-11). Extend CATALOGUED as each
// agreement is catalogued from a real source (Master Build Plan, Plan 2).
//
// Today: Amtrak Yardmaster only (AMTRAK_BASE_RULES, sourced Nov 1 2000 printout). D&H, Conrail
// YM, Conrail M/S, CSX, and BN are NOT catalogued, so their AI-suggested quotes are suppressed.
// Ruling 312 phase 1 (d), 2026-10-17: the agreement is named by src\data\agreementRegistry.js; these are its families.
import { agreementFamily } from './agreementRegistry';   // ruling 312 phase 1 (d)
const CATALOGUED = ['amtrak-ym'];

export function isCatalogued(agreement) {
  return !!agreement && CATALOGUED.includes(agreementFamily(agreement));
}

// Agreements that have a REAL rule catalog to drive the picker (verified titles + sourced text),
// even if their body text is not yet fully verified. Broader than isCatalogued: an agreement can
// have a picker catalog (so members pick real rules) while its AI-suggested QUOTES are still
// suppressed (isCatalogued=false) until the text is line-verified. Conrail YM 1978 is the first
// such: rules 1-9 catalogued from the sourced scan; body text OCR-derived, shown with a caveat.
const PICKER_CATALOGUED = ['conrail-ym', 'csx-ym', 'dh-ym', 'bn-ym', 'conrail-ms'];   // Conrail M/S added 2026-09-30 (section 522): Rules 1-29 from the 1994 schedule, 1995-round modifications layered   // BN added 2026-09-14 (G8.5, section 231): rules 1-31 verbatim from the 1997 schedule's own text layer

export function hasRuleCatalog(agreement) {
  return isCatalogued(agreement) || (!!agreement && PICKER_CATALOGUED.includes(agreementFamily(agreement)));
}

// ONE STANDARD (Bob, 2026-08-07): no UNSOURCED AI text persists on any claim. Until 2026-08-22 that
// meant stripping exactLanguage, fullTitle and relevance from every non-catalogued agreement, because
// analyzeClaimRules AUTHORED that text and nothing could vouch for it.
//
// WIDENED 2026-08-22 on Bob's ruling, and the standard itself is unchanged — the facts underneath it
// changed. Rule analysis now returns a violation id and a line; the Cloud Function fills the quote in
// from data\\YCP_VIOLATION_RULE_MAP.csv, every row of which VIO-1 has proved resolves verbatim in its
// cited file at its cited line. The model never authors agreement language, so a rule that comes back
// marked `sourced` is not AI text at all — it is the map's text, carrying its own file and line.
//
// THE GATE IS NOW PER RULE, NOT PER AGREEMENT, and that is deliberate. An agreement with no entry in
// the violation brief still falls back to label matching, and on that path the model DOES author the
// quote. Widening by agreement would let exactly that text through. So the test is what the rule
// itself carries: `sourced === true` AND a violationId AND a sourceFile AND a sourceLine. Anything
// short of that is stripped exactly as before.
function isSourcedRule(r) {
  return !!r && r.sourced === true
    && typeof r.violationId === 'string' && r.violationId.length > 0
    && typeof r.sourceFile === 'string' && r.sourceFile.length > 0
    && (typeof r.sourceLine === 'string' || typeof r.sourceLine === 'number')
    && String(r.sourceLine).length > 0;
}

export function sanitizeMatchedRules(rules, agreement) {
  if (!Array.isArray(rules)) return rules;
  if (isCatalogued(agreement)) return rules.filter(onePerRule());
  return rules.map(function (r) {
    if (isSourcedRule(r)) return r;          // map-sourced: keep the quote, its file and its line
    const clean = Object.assign({}, r);
    delete clean.exactLanguage;
    delete clean.fullTitle;
    delete clean.relevance;
    return clean;
  }).filter(onePerRule());
}

// One entry per rule label (Bob 2026-10-14, "the same rule twice?"); the first kept. Mirrors ruleCatalog.onePerRule.
function onePerRule() {
  const seen = new Set();
  return function (item) {
    const k = String((item && item.rule) || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!k) return true;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  };
}
